import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const StatusEnum = z.enum(["pending", "confirmed", "rejected"]);
const SeverityEnum = z.enum(["low", "medium", "high", "critical"]);
const SortEnum = z.enum(["detected_at", "severity", "amount", "workspace"]);

const SEVERITY_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };

const ListInput = z.object({
  workspace_id: z.string().uuid().optional().nullable(),
  statuses: z.array(StatusEnum).default(["pending"]),
  severities: z.array(SeverityEnum).optional(),
  time_range: z.enum(["24h", "7d", "30d", "all"]).default("30d"),
  search: z.string().optional(),
  sort_by: SortEnum.default("detected_at"),
  sort_dir: z.enum(["asc", "desc"]).default("desc"),
  limit: z.number().int().min(1).max(500).default(200),
});

export const listAnomalyInbox = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ListInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;

    let q = supabase
      .from("anomalies")
      .select(
        "id, workspace_id, anomaly_type, severity, status, title, explanation_body, recommended_actions, detection_payload, affected_contact_ids, detected_at, acknowledged_at, resolved_at, notes, workspaces(program_name)",
      )
      .in("status", data.statuses);

    if (data.workspace_id) q = q.eq("workspace_id", data.workspace_id);
    if (data.severities?.length) q = q.in("severity", data.severities);

    if (data.time_range !== "all") {
      const days = data.time_range === "24h" ? 1 : data.time_range === "7d" ? 7 : 30;
      const since = new Date(Date.now() - days * 86400000).toISOString();
      q = q.gte("detected_at", since);
    }

    if (data.search?.trim()) {
      const s = data.search.trim().replace(/[%_]/g, "");
      q = q.or(`title.ilike.%${s}%,explanation_body.ilike.%${s}%,anomaly_type.ilike.%${s}%`);
    }

    q = q.limit(data.limit);

    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const dir = data.sort_dir === "asc" ? 1 : -1;
    const sorted = [...(rows ?? [])].sort((a: any, b: any) => {
      switch (data.sort_by) {
        case "severity":
          return ((SEVERITY_RANK[a.severity] ?? 0) - (SEVERITY_RANK[b.severity] ?? 0)) * dir;
        case "amount": {
          const av = Number(a.detection_payload?.value_at_risk ?? a.detection_payload?.at_risk ?? a.detection_payload?.total_value_per_cycle ?? 0);
          const bv = Number(b.detection_payload?.value_at_risk ?? b.detection_payload?.at_risk ?? b.detection_payload?.total_value_per_cycle ?? 0);
          return (av - bv) * dir;
        }
        case "workspace":
          return String(a.workspaces?.program_name ?? "").localeCompare(String(b.workspaces?.program_name ?? "")) * dir;
        case "detected_at":
        default:
          return (new Date(a.detected_at).getTime() - new Date(b.detected_at).getTime()) * dir;
      }
    });

    return { anomalies: sorted };
  });

const TransitionInput = z.object({
  anomaly_id: z.string().uuid(),
  action: z.enum(["acknowledge", "resolve", "dismiss", "reopen"]),
});

export const transitionAnomaly = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => TransitionInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: a, error: aerr } = await supabase
      .from("anomalies")
      .select("id, workspace_id, anomaly_type, status, workspaces(account_id)")
      .eq("id", data.anomaly_id)
      .single();
    if (aerr || !a) throw new Error("Not found");

    const now = new Date().toISOString();
    const patch: Record<string, any> = {};
    if (data.action === "acknowledge") {
      patch.acknowledged_at = now;
      patch.acknowledged_by_user_id = userId;
    } else if (data.action === "resolve") {
      patch.status = "confirmed";
      patch.resolved_at = now;
      patch.resolved_by_user_id = userId;
    } else if (data.action === "dismiss") {
      patch.status = "rejected";
      patch.resolved_at = now;
      patch.resolved_by_user_id = userId;
    } else if (data.action === "reopen") {
      patch.status = "pending";
      patch.resolved_at = null;
      patch.resolved_by_user_id = null;
    }

    const { error: uerr } = await supabaseAdmin.from("anomalies").update(patch as never).eq("id", data.anomaly_id);
    if (uerr) throw new Error(uerr.message);

    await supabase.from("activity_events").insert({
      account_id: (a.workspaces as any)?.account_id,
      workspace_id: a.workspace_id,
      actor_user_id: userId,
      event_type: `anomaly_${data.action}`,
      description: `Anomaly ${a.anomaly_type} ${data.action}d`,
      metadata_json: { anomaly_id: data.anomaly_id },
    });

    return { ok: true };
  });

const ContactsInput = z.object({ anomaly_id: z.string().uuid() });

export const getAnomalyContacts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ContactsInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: a, error: aerr } = await supabase
      .from("anomalies")
      .select("workspace_id, affected_contact_ids")
      .eq("id", data.anomaly_id)
      .single();
    if (aerr || !a) throw new Error("Not found");
    const ids = (a.affected_contact_ids ?? []) as string[];
    if (!ids.length) return { contacts: [] };
    const { data: rows, error } = await supabase
      .from("contacts")
      .select("id, email, first_name, last_name, status, commission_amount")
      .eq("workspace_id", a.workspace_id)
      .in("id", ids)
      .limit(500);
    if (error) throw new Error(error.message);
    return { contacts: rows ?? [] };
  });

const NoteInput = z.object({
  anomaly_id: z.string().uuid(),
  body: z.string().min(1).max(2000),
});

export const addAnomalyNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => NoteInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: a } = await supabase.from("anomalies").select("notes").eq("id", data.anomaly_id).single();
    if (!a) throw new Error("Not found");
    const notes = Array.isArray(a.notes) ? (a.notes as any[]) : [];
    notes.push({ user_id: userId, body: data.body.trim(), created_at: new Date().toISOString() });
    const { error } = await supabaseAdmin.from("anomalies").update({ notes }).eq("id", data.anomaly_id);
    if (error) throw new Error(error.message);
    return { ok: true, notes };
  });
